import { formatCurrency, formatNumber } from '@angular/common';
import {EventEmitter, Inject, Injectable, LOCALE_ID} from '@angular/core';
import { Currency } from '../../common/organisation';

export interface FormatSettings {
	currencySymbol: number | number[]
}

// Default values for formatting modeles
export const DefaultFormatSettings: FormatSettings = {
	currencySymbol: 0x0024
};

// shared by every instance: the service is provided by more than one module, and all of them must show the same
// currency symbol
const singletonFormattingService: {
	currentFormatSettings: FormatSettings,
	numberFormatter: { format: (value: number) => string },
	numberFormatterDecimal: { format: (value: number) => string },
	onFormattingChanged: EventEmitter<FormatSettings>
} = {
	numberFormatter: new Intl.NumberFormat('en', {maximumFractionDigits: 0}),
	numberFormatterDecimal: new Intl.NumberFormat('en', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2
	}),
	currentFormatSettings: DefaultFormatSettings,
	onFormattingChanged: new EventEmitter()
};

// a number to show, or undefined for a missing one; numbers held as text (as some data is) are read as numbers
const toNumber = (value:unknown):number|undefined => {
	const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : value
	return typeof n === 'number' && isFinite(n) ? n : undefined
}

@Injectable()
export class FormattingService {


	public onFormattingChanged: EventEmitter<FormatSettings>;

	constructor(@Inject(LOCALE_ID) public locale: string) {
		this.onFormattingChanged = singletonFormattingService.onFormattingChanged;
	}

	public get formatSettings(): FormatSettings {
		return singletonFormattingService.currentFormatSettings
	}

	public set formatSettings(settings: FormatSettings) {
		singletonFormattingService.currentFormatSettings = settings
		this.onFormattingChanged.emit(this.formatSettings);
	}

    // whole numbers as they are, others to two decimal places (unless decimals aren't wanted); nothing for a missing value
    public formatNumber(value: number, allowDecimal: boolean = true, forceDecimal: boolean = false): string {
		const num = toNumber(value)
		if (num === undefined) {
			return '';
		}

		if (forceDecimal || (((num % 1) !== 0) && allowDecimal)) {
			return singletonFormattingService.numberFormatterDecimal.format(num);
		}

		return singletonFormattingService.numberFormatter.format(num);
    }

	// as formatNumber, with the currency symbol; a negative amount is written -$1,234 rather than $-1,234
	public formatCost(value: number, allowDecimal: boolean = true, forceDecimal: boolean = false): string {
		const cost = toNumber(value)
		if (cost === undefined) {
			return '';
		}

		const sign = cost < 0 ? '-' : ''
		return sign + this.currencySymbol + this.formatNumber(Math.abs(cost), allowDecimal, forceDecimal);
	}

	public get currencySymbol(): string {

		const symbolValues: number | number[] = singletonFormattingService.currentFormatSettings.currencySymbol;
		if (typeof symbolValues === 'number') {
			return String.fromCharCode(symbolValues);
		}

		return symbolValues.reduce((accum, symbolValue) => {
			return accum + String.fromCharCode(symbolValue);
		}, '');
	}

	// short enough for a label: 12.3K and 2.35M from ten thousand up, negative amounts too; nothing for a missing value
	formatAbbreviatedNumber(input:number, currency?:Currency):string {
        const value = toNumber(input)
        if (value === undefined) return ''

        const size = Math.abs(value)
        let num = Math.round(value)
        let suffix = ""
        // just under a million rounds to a thousand thousands, which is written as a million
        if (size >= 999500) {
            num = Math.round(value/10000)/100
            suffix = "M"
        }else if (size >= 10000) {
            num = Math.round(value/100)/10
            suffix = "K"
        }
        // shortened amounts keep their decimals, as plain numbers do
        const digits = suffix ? '1.0-2' : '1.0-0'
        return (currency?formatCurrency(num, this.locale, currency.symbol, currency.code, digits):formatNumber(num, this.locale, digits)) + suffix
    }
}